import PersonAdd from '@mui/icons-material/PersonAdd';
import Button from '@mui/material/Button';
import { useCallback, useEffect, useState } from 'react';
import { instance } from '../../axios/AxiosInstance';
import AppLayout from '../../components/AppLayout/AppLayout';
import { getRoleLabel } from '../../utils/roleLabel';
import './ManagerUsers.scss';

/** Mirrors glubes-api `GlubesUserModel`. List responses clear password / reset fields. */
export interface ManagerUserRow {
  email: string;
  phone: string;
  role: string;
  first_name: string;
  last_name: string;
  /** When omitted (legacy rows), treated as active in the UI. */
  active?: boolean;
  created_at?: string;
  version?: string;
  last_login_at?: string;
}

function formatUserDate(iso: string | undefined): string {
  if (iso == null || String(iso).trim() === '') return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('mn-MN', { dateStyle: 'short', timeStyle: 'short' });
}

interface ListResponse {
  data: ManagerUserRow[];
}

export default function ManagerUsers() {
  const [users, setUsers] = useState<ManagerUserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteModal, setDeleteModal] = useState<{ open: true; user: ManagerUserRow } | { open: false; user: null }>({
    open: false,
    user: null,
  });
  const [deleting, setDeleting] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await instance.get<ListResponse>('managers');
      setUsers(data.data ?? []);
    } catch {
      setError('Хэрэглэгчдийн жагсаалт ачааллахад алдаа гарлаа.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleDeleteConfirm = async () => {
    if (!deleteModal.open || !deleteModal.user) return;
    setDeleting(true);
    try {
      await instance.delete('managers', { data: { email: deleteModal.user.email } });
      setDeleteModal({ open: false, user: null });
      await fetchUsers();
    } catch {
      setError('Хэрэглэгчийг устгахад алдаа гарлаа.');
    } finally {
      setDeleting(false);
    }
  };

  const openDeleteModal = (u: ManagerUserRow) => setDeleteModal({ open: true, user: u });
  const closeDeleteModal = () => setDeleteModal({ open: false, user: null });

  return (
    <AppLayout>
      <div className="manager-users">
        <div className="manager-users__header">
          <h1 className="manager-users__title">Хэрэглэгчийн удирдлага</h1>
          <Button
            variant="contained"
            color="primary"
            startIcon={<PersonAdd />}
            onClick={() => window.open('/manager/users/new', '_blank', 'noopener,noreferrer')}
          >
            Хэрэглэгч нэмэх
          </Button>
        </div>

        {error && (
          <p className="manager-users__error" role="alert">
            {error}
          </p>
        )}

        {loading ? (
          <p className="text-sub">Жагсаалт ачааллаж байна...</p>
        ) : (
          <div className="manager-users__table-wrap">
            <table className="manager-users__table">
              <thead>
                <tr>
                  <th className="manager-users__th">Имэйл</th>
                  <th className="manager-users__th">Нэр</th>
                  <th className="manager-users__th">Утас</th>
                  <th className="manager-users__th">Эрх</th>
                  <th className="manager-users__th">Төлөв</th>
                  <th className="manager-users__th">Сүүлийн нэвтрэлт</th>
                  <th className="manager-users__th manager-users__th--actions" aria-label="Үйлдэл" />
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="manager-users__empty">
                      Хэрэглэгч олдсонгүй
                    </td>
                  </tr>
                ) : (
                  users.map((u) => (
                    <tr key={u.email} className="manager-users__row">
                      <td className="manager-users__td">{u.email}</td>
                      <td className="manager-users__td">
                        {u.first_name} {u.last_name}
                      </td>
                      <td className="manager-users__td">{u.phone?.trim() ? u.phone : '—'}</td>
                      <td className="manager-users__td">{getRoleLabel(u.role)}</td>
                      <td className="manager-users__td">{u.active === false ? 'Идэвхгүй' : 'Идэвхтэй'}</td>
                      <td className="manager-users__td">{formatUserDate(u.last_login_at)}</td>
                      <td className="manager-users__td manager-users__td--actions">
                        <button
                          type="button"
                          className="manager-users__icon-btn"
                          onClick={() =>
                            window.open(
                              `/manager/users/${encodeURIComponent(u.email)}`,
                              '_blank',
                              'noopener,noreferrer',
                            )
                          }
                          title="Засах"
                          aria-label="Засах"
                        >
                          <span className="material-symbols-outlined">edit</span>
                        </button>
                        <button
                          type="button"
                          className="manager-users__icon-btn manager-users__icon-btn--danger"
                          onClick={() => openDeleteModal(u)}
                          title="Устгах"
                          aria-label="Устгах"
                        >
                          <span className="material-symbols-outlined">delete</span>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {deleteModal.open && deleteModal.user && (
          <div className="manager-users__modal-overlay" role="dialog" aria-modal="true" aria-labelledby="delete-modal-title">
            <div className="manager-users__modal">
              <h2 id="delete-modal-title" className="manager-users__modal-title">
                Хэрэглэгчийг устгах уу?
              </h2>
              <p className="manager-users__modal-text">
                &quot;{deleteModal.user.first_name} {deleteModal.user.last_name}&quot; ({deleteModal.user.email}) бүртгэлийг устгахдаа итгэлтэй байна уу? Энэ үйлдлийг буцааж болохгүй.
              </p>
              <div className="manager-users__modal-actions">
                <button type="button" className="btn-ghost" onClick={closeDeleteModal} disabled={deleting}>
                  Цуцлах
                </button>
                <button
                  type="button"
                  className="btn-primary manager-users__modal-delete-btn"
                  onClick={handleDeleteConfirm}
                  disabled={deleting}
                >
                  {deleting ? 'Устгаж байна...' : 'Устгах'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
